/**
 * One person's record: their rate, their distribution, every session behind it, and
 * everything written down about them.
 *
 * The history is editable in place, because the reason to open this page is usually to fix a
 * mark that was set wrong or left blank. The notes are collected above it in their own list,
 * because reading them back is a different job from correcting a session.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { notesAbout, personNote } from '../../shared/notes.js';
import {
  addState,
  emptyCounts,
  formatRate,
  presentRate,
  recordedCount,
  slotCount,
  stateAt,
} from '../../shared/stats.js';
import { AttendanceState, type IsoDate, type Note } from '../../shared/types.js';
import { removePerson, setMark, setNote, updatePerson } from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { usePerson } from '../data/selectors.js';
import { RECORDING_CYCLES, StateChoice, StateFigures, appearanceOf } from '../ui/attendance.js';
import { StatusBar } from '../ui/charts/StatusBar.js';
import { formatMediumDate, plural } from '../ui/format.js';
import { CommentIcon, PencilIcon, TrashIcon } from '../ui/icons.js';
import { NoteBlock, NoteButton, NoteForm } from '../ui/notes.js';
import { Button, Dialog, EmptyState, TextField } from '../ui/primitives.js';
import { ProjectFrame } from '../ui/Shell.js';

/** Number of sessions shown before the history collapses behind a control. */
const HISTORY_PAGE = 30;

/** One session in the history, with whatever was recorded and written about it. */
interface HistoryEntry {
  date: IsoDate;
  state: AttendanceState | undefined;
  note: Note | undefined;
}

/** Renders one person's page. */
export function PersonPage(): ReactNode {
  const { projectId, personId } = useParams();
  const store = useStore();
  const navigate = useNavigate();
  const derived = useProjectDerived(projectId);
  const person = usePerson(personId);
  const [editing, setEditing] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [shown, setShown] = useState(HISTORY_PAGE);
  const [openNote, setOpenNote] = useState<IsoDate | null>(null);

  const history = useMemo((): HistoryEntry[] => {
    if (person === undefined || derived === null) return [];
    return [...derived.pastSessions].reverse().map((date) => ({
      date,
      state: stateAt(derived.marks, person.id, date),
      note: personNote(derived.notes, derived.project.id, person.id, date),
    }));
  }, [derived, person]);

  const written = useMemo(() => {
    if (person === undefined || derived === null) return [];
    return [...notesAbout(derived.notes, person.id)].reverse();
  }, [derived, person]);

  const counts = useMemo(() => {
    const totals = emptyCounts();
    for (const entry of history) addState(totals, entry.state);
    return totals;
  }, [history]);

  if (derived === null || person === undefined) {
    return <ProjectFrame><EmptyState message="That person is not on this device." /></ProjectFrame>;
  }

  const { project } = derived;

  const rate = presentRate(counts, project.countExcusedAsPresent);

  return (
    <ProjectFrame>
      <header className="flex items-start justify-between gap-3 px-4 pt-5 pb-4">
        <div className="min-w-0">
          <h1 className="text-headline font-bold break-words">{person.name}</h1>
          {person.note === '' ? null : <p className="mt-1 text-ink-2">{person.note}</p>}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button onClick={() => setEditing(true)}><PencilIcon size={18} />Edit</Button>
        </div>
      </header>

      <section className="border-y border-line px-4 py-5">
        <p className="tabular text-hero leading-none font-bold text-brand-text">{formatRate(rate)}</p>
        <p className="mt-2 max-w-prose text-ink-2">
          present across {recordedCount(counts)} recorded{' '}
          {plural(recordedCount(counts), 'session', 'sessions')}
          {counts.unset > 0 ? `, ${counts.unset} of ${slotCount(counts)} still unrecorded` : ''}.
        </p>
        <div className="mt-5"><StatusBar counts={counts} height={10} /></div>
        <div className="mt-3"><StateFigures counts={counts} /></div>
      </section>

      {written.length === 0 ? null : (
        <section className="border-b border-line px-4 py-3">
          <ul>
            {written.map((note) => (
              <li
                key={note.id}
                className="grid grid-cols-1 gap-x-4 border-b border-line py-2 last:border-b-0
                  sm:grid-cols-[7rem_minmax(0,1fr)]"
              >
                <p className="pt-1 text-tiny font-semibold text-ink-2">{formatMediumDate(note.date)}</p>
                <NoteBlock
                  label={`Note about ${person.name} on ${formatMediumDate(note.date)}`}
                  addLabel="Write a note"
                  placeholder={`Anything worth remembering about ${person.name}.`}
                  note={note}
                  onSave={(text) => void setNote(store, project.id, note.date, person.id, text)}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {history.length === 0 ? (
        <EmptyState message="No sessions have happened yet." />
      ) : (
        <ul className="border-t border-line">
          {history.slice(0, shown).map((entry) => (
            <li key={entry.date} className="border-b border-line bg-surface">
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-sunken">
                <div className="min-w-0">
                  <p className="text-row font-medium">{formatMediumDate(entry.date)}</p>
                  <p className={`text-tiny ${appearanceOf(entry.state ?? 'unset').text}`}>
                    {appearanceOf(entry.state ?? 'unset').label}
                  </p>
                  {entry.note === undefined ? null : (
                    <p className="flex items-center gap-1.5 truncate text-tiny text-ink-2">
                      <CommentIcon size={13} className="shrink-0 text-ink-3" />
                      {entry.note.text}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StateChoice
                    groupLabel={`Attendance on ${formatMediumDate(entry.date)}`}
                    current={entry.state}
                    cycles={RECORDING_CYCLES}
                    onSet={(next) => void setMark(store, project.id, person.id, entry.date, next)}
                  />
                  <NoteButton
                    label={`${person.name} on ${formatMediumDate(entry.date)}`}
                    hasNote={entry.note !== undefined}
                    expanded={openNote === entry.date}
                    onClick={() => setOpenNote(openNote === entry.date ? null : entry.date)}
                  />
                </div>
              </div>
              {openNote === entry.date ? (
                <div className="border-t border-line px-4 py-3">
                  <NoteForm
                    label={`Note about ${person.name} on ${formatMediumDate(entry.date)}`}
                    value={entry.note?.text ?? ''}
                    placeholder={`Anything worth remembering about ${person.name} that day.`}
                    onSave={(text) => void setNote(store, project.id, entry.date, person.id, text)}
                    onCancel={() => setOpenNote(null)}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {shown < history.length ? (
        <div className="px-4 py-4">
          <Button full onClick={() => setShown(shown + HISTORY_PAGE)}>
            Show {Math.min(HISTORY_PAGE, history.length - shown)} earlier sessions
          </Button>
        </div>
      ) : null}

      <section className="border-t border-line px-4 py-6">
        <Button tone="danger" onClick={() => setConfirmingRemoval(true)}>
          <TrashIcon size={18} />
          Remove from the roster
        </Button>
      </section>

      {editing ? (
        <EditPersonDialog
          name={person.name}
          note={person.note}
          onClose={() => setEditing(false)}
          onSave={(name, note) => void updatePerson(store, person.id, { name, note })}
        />
      ) : null}

      {confirmingRemoval ? (
        <Dialog
          title={`Remove ${person.name}?`}
          onClose={() => setConfirmingRemoval(false)}
          footer={
            <>
              <Button onClick={() => setConfirmingRemoval(false)}>Cancel</Button>
              <Button
                tone="danger"
                onClick={() => {
                  void removePerson(store, person.id);
                  void navigate(`/p/${project.id}/people`);
                }}
              >
                Remove
              </Button>
            </>
          }
        >
          <p>
            {person.name} will no longer appear in roll calls. Their {recordedCount(counts)} recorded{' '}
            {plural(recordedCount(counts), 'mark', 'marks')} stay in the project's statistics.
          </p>
        </Dialog>
      ) : null}
    </ProjectFrame>
  );
}

/** Form for renaming a person and editing their note. */
function EditPersonDialog({
  name,
  note,
  onClose,
  onSave,
}: {
  name: string;
  note: string;
  onClose: () => void;
  onSave: (name: string, note: string) => void;
}): ReactNode {
  const [draftName, setDraftName] = useState(name);
  const [draftNote, setDraftNote] = useState(note);

  return (
    <Dialog
      title="Edit person"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            tone="primary"
            disabled={draftName.trim() === ''}
            onClick={() => { onSave(draftName, draftNote); onClose(); }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField label="Name" value={draftName} onChange={setDraftName} autoFocus />
        <TextField
          label="Note"
          value={draftNote}
          onChange={setDraftNote}
          placeholder="Group, contact, anything useful"
        />
      </div>
    </Dialog>
  );
}
